/**
 * Object-track client buffer — a Zustand store of the live **object tracks**
 * (ADR-041), the single source feeding the Map's track polyline layer and the
 * Tracks panel (REQ-UI-167 / -170 / -178).
 *
 * # Vocabulary
 *
 * An **object track** is the series of detections one sensor reported under
 * one BSI ``object_id`` — keyed by ``track_id`` (UUIDv5 over
 * ``(producer_id, object_id)``, derived by the BFF and carried on every
 * detection item and ``detection`` SSE payload; REQ-QUERY-053). It is *not*
 * the per-Producer position tail in :file:`map/track-store.ts` — that is a
 * platform's own recent path ("Platform trails"); this is what a sensor saw.
 *
 * # Three feeders, one buffer (the platform-tail shape)
 *
 *   - **Recent poll** — :func:`object-tracks/use-recent-tracks-feed` seeds
 *     the summaries from ``GET /api/query/recent-tracks`` (the Tracks panel's
 *     global feed).
 *   - **Viewport backfill** — :func:`map/use-object-tracks-feed` seeds
 *     summaries **and members** from ``GET /api/query/object-tracks`` on load
 *     / after a pan, so lines appear fully-formed.
 *   - **Live SSE** — the detections SSE bridge appends each ``detection``
 *     frame as a member of its ``track_id``, so a head moves and a line grows
 *     on the same frame the glyph does.
 *
 * # Members, not points
 *
 * A member carries the same detection subset the SSE payload carries,
 * **positioned or not** (REQ-INGEST-044): the Tracks panel's expanded rows list
 * every member; the map line uses :func:`positionedMembers`. Members are kept
 * time-ascending and de-duplicated by ``report_id`` so a backfilled member and
 * its live-appended twin converge on one row (the detections buffer's
 * ``report_id`` discipline).
 *
 * # Retention
 *
 * A track is dropped once its **head** (newest member, else the summary's
 * ``last_time``) ages past the live window (REQ-UI-178 / -181 — the
 * deployment's ``live.window_seconds``, passed in by the feeders; the BFF
 * default until it resolves). Per-track members ring to
 * :data:`TRACK_MAX_MEMBERS`; the buffer caps at :data:`OBJECT_TRACK_BUFFER_CAP`
 * tracks keeping the newest heads.
 *
 * # The batch path (feature spec ``ui-track-load-headroom`` §2.4)
 *
 * The live bridge commits one batch per animation frame through
 * :func:`appendMany` (REQ-UI-192). A member carries its detection time parsed
 * once at ingest (``time_ms``); an in-order member — the overwhelmingly common
 * case — is a push onto its track's array, an out-of-order one a binary
 * insert, so a commit costs O(batch), never a re-sort (REQ-UI-194). The
 * reverse index ``trackIdByReportId`` is maintained incrementally (an id is
 * added on append and deleted on ring-trim or prune) and is **mutated in
 * place** across commits: it is a lookup table read by key through
 * :func:`useTrackIdForDetection`, never iterated by a component, and copying
 * ~100k entries per frame was the cost the maintained index removes.
 *
 * Cross-references:
 *   - REQ-UI-027 (Zustand for client state); REQ-UI-167 / -170 / -178 / -181.
 *   - REQ-QUERY-050 / -051 / -053 (the feeds and the id).
 *   - ADR-041 D-1 (identity), D-4 (map), D-7 (the live window).
 *   - map/track-store.ts (the sibling this mirrors — platform tails).
 */
import { useMemo } from "react";
import { create } from "zustand";
import { DEFAULT_LIVE_WINDOW_SECONDS } from "@/chrome/use-deployment-config";
import type { components } from "@/lib/api-types.gen";

/** The wire summary (``TrackSummaryItem``) — the BFF's shape, used as-is. */
export type TrackSummary = components["schemas"]["TrackSummaryItem"];

/** One member detection of a track — the SSE detection subset (REQ-QUERY-051). */
export interface TrackMember {
  readonly report_id: string;
  /** Detection timestamp, ISO-8601 UTC. */
  readonly time: string;
  /**
   * ``time`` parsed to epoch ms, stamped by the store at ingest so ordering,
   * pruning and fading never re-parse. Absent only on a member built outside
   * the store (a test literal); :func:`memberTimeMs` falls back to parsing.
   */
  readonly time_ms?: number;
  readonly latitude_deg: number | null;
  readonly longitude_deg: number | null;
  readonly elevation_m: number | null;
  readonly primary_class: string | null;
  readonly detection_confidence: number | null;
  readonly state: string | null;
}

/** One object track as the client holds it. */
export interface ObjectTrackRecord {
  readonly track_id: string;
  readonly producer_id: string;
  readonly object_id: string;
  /** The BFF summary when a REST feed has seen the track; ``null`` when only live frames have. */
  readonly summary: TrackSummary | null;
  /** Every member seen, time-ascending, unique by ``report_id``; ring-capped. */
  readonly members: readonly TrackMember[];
}

/** Members per track kept in memory (the newest are kept). */
export const TRACK_MAX_MEMBERS = 500;

/** Tracks kept in memory (the newest heads are kept). */
export const OBJECT_TRACK_BUFFER_CAP = 300;

/** The pre-config live window (the BFF default), in milliseconds. */
export const DEFAULT_LIVE_WINDOW_MS = DEFAULT_LIVE_WINDOW_SECONDS * 1000;

/** Optional knobs for the mutating actions. */
export interface TrackMutationOptions {
  /** The reference "now" (epoch ms) for pruning; defaults to ``Date.now()``. */
  readonly now?: number;
  /** The live window (ms); defaults to :data:`DEFAULT_LIVE_WINDOW_MS`. */
  readonly retentionMs?: number;
}

/** The identity a live frame or a backfill names a track by. */
export interface TrackKey {
  readonly track_id: string;
  readonly producer_id: string;
  readonly object_id: string;
}

/** One live member to append — the unit of :func:`ObjectTracksState.appendMany`. */
export interface AppendEntry {
  readonly key: TrackKey;
  readonly member: TrackMember;
}

/** The store shape. */
export interface ObjectTracksState {
  /** Tracks keyed by ``track_id``. */
  readonly byId: Record<string, ObjectTrackRecord>;
  /**
   * ``report_id`` → ``track_id`` — the reverse index the panel's
   * selection-driven expansion reads. Maintained incrementally and mutated in
   * place across commits (see the module docstring); read it by key, never
   * iterate it from a component.
   */
  readonly trackIdByReportId: Record<string, string>;
  /** Append (or refresh) one member of a track from a live frame. */
  readonly append: (key: TrackKey, member: TrackMember, opts?: TrackMutationOptions) => void;
  /**
   * Append a frame's worth of live members in arrival order — one commit, one
   * notification (REQ-UI-192). Equivalent to calling :func:`append` for each
   * entry in sequence.
   */
  readonly appendMany: (entries: readonly AppendEntry[], opts?: TrackMutationOptions) => void;
  /** Merge a backfilled track — summary and members — into the buffer. */
  readonly seed: (
    key: TrackKey,
    summary: TrackSummary | null,
    members: readonly TrackMember[],
    opts?: TrackMutationOptions,
  ) => void;
  /** Merge a batch of summaries (the recent poll); members are untouched. */
  readonly seedSummaries: (summaries: readonly TrackSummary[], opts?: TrackMutationOptions) => void;
  /** Empty the buffer (sign-out / network switch). */
  readonly clear: () => void;
}

/**
 * A member's time as the order key (epoch ms). An unparseable ``time`` sorts
 * oldest rather than poisoning every comparison with ``NaN``.
 */
function parseTimeMs(time: string): number {
  const parsed = Date.parse(time);
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
}

/** The member's detection time in epoch ms — the stamp when present, else parsed. */
export function memberTimeMs(member: TrackMember): number {
  return member.time_ms ?? parseTimeMs(member.time);
}

/** The member with its ``time_ms`` stamp (a no-op when already stamped). */
function stamped(member: TrackMember): TrackMember {
  return member.time_ms === undefined ? { ...member, time_ms: parseTimeMs(member.time) } : member;
}

/** The newest member of a track, or ``null`` when it has none. */
export function headOf(record: ObjectTrackRecord): TrackMember | null {
  return record.members.length === 0 ? null : record.members[record.members.length - 1];
}

/** The head's time (epoch ms): the newest member, else the summary's ``last_time``. */
export function headTimeMs(record: ObjectTrackRecord): number {
  const head = headOf(record);
  if (head !== null) {
    return memberTimeMs(head);
  }
  return record.summary === null ? Number.NEGATIVE_INFINITY : Date.parse(record.summary.last_time);
}

/**
 * When the track began (epoch ms): the BFF summary's ``first_time`` when a REST
 * feed has seen the track, else the oldest member the client holds.
 * ``NEGATIVE_INFINITY`` when neither is known. The summary is preferred because
 * the client's oldest *held* member can move earlier when a backfill merges
 * older members in, and the Tracks panel's tie-break (``track-sort.ts``) wants
 * a key that never changes while the track lives.
 */
export function firstTimeMs(record: ObjectTrackRecord): number {
  if (record.summary !== null) {
    const parsed = Date.parse(record.summary.first_time);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }
  return record.members.length === 0 ? Number.NEGATIVE_INFINITY : memberTimeMs(record.members[0]);
}

/** How many members the client believes the track has (the larger of what it holds and what the BFF said). */
export function memberCount(record: ObjectTrackRecord): number {
  return Math.max(record.members.length, record.summary?.detection_count ?? 0);
}

/** Whether a record is *presented* as a track — two or more members (ADR-041 D-1). */
export function isPresentedTrack(record: ObjectTrackRecord): boolean {
  return memberCount(record) >= 2;
}

/** The members with a position, in time order — the line's vertices. */
export function positionedMembers(record: ObjectTrackRecord): readonly TrackMember[] {
  return record.members.filter((m) => m.latitude_deg !== null && m.longitude_deg !== null);
}

/** Total order on members: (time, report_id). */
function compareMembers(a: TrackMember, b: TrackMember): number {
  const dt = memberTimeMs(a) - memberTimeMs(b);
  if (dt !== 0) {
    return dt;
  }
  return a.report_id < b.report_id ? -1 : a.report_id > b.report_id ? 1 : 0;
}

/**
 * The seed path's merge: union by ``report_id`` (a colliding member describes
 * the same detection, so the incoming copy wins — the REST feeds carry the
 * fuller row), sorted by (time, report_id), then rung. Returns the members
 * the ring dropped so the caller can un-index them.
 */
function mergeMembers(
  existing: readonly TrackMember[],
  incoming: readonly TrackMember[],
): { members: TrackMember[]; dropped: readonly TrackMember[] } {
  const byReport = new Map<string, TrackMember>();
  for (const m of existing) byReport.set(m.report_id, stamped(m));
  for (const m of incoming) byReport.set(m.report_id, stamped(m));
  const merged = [...byReport.values()].sort(compareMembers);
  if (merged.length > TRACK_MAX_MEMBERS) {
    const dropped = merged.splice(0, merged.length - TRACK_MAX_MEMBERS);
    return { members: merged, dropped };
  }
  return { members: merged, dropped: [] };
}

/** First index whose member sorts after ``member`` (the insertion point). */
function upperBound(members: readonly TrackMember[], member: TrackMember): number {
  let low = 0;
  let high = members.length;
  while (low < high) {
    const mid = (low + high) >>> 1;
    if (compareMembers(members[mid], member) <= 0) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }
  return low;
}

/**
 * Place one stamped live member into its track's working array: the in-order
 * fast path is a push; a known ``report_id`` is replaced in place (or moved
 * when its time changed); anything else binary-inserts.
 */
function placeMember(members: TrackMember[], member: TrackMember, known: boolean): void {
  if (known) {
    const existingIndex = members.findIndex((m) => m.report_id === member.report_id);
    if (existingIndex !== -1) {
      if (compareMembers(members[existingIndex], member) === 0) {
        members[existingIndex] = member;
        return;
      }
      members.splice(existingIndex, 1);
    }
  }
  const last = members[members.length - 1];
  if (last === undefined || compareMembers(last, member) <= 0) {
    members.push(member);
    return;
  }
  members.splice(upperBound(members, member), 0, member);
}

/** Drop a track's members from the reverse index. */
function unindex(index: Record<string, string>, members: readonly TrackMember[]): void {
  for (const m of members) {
    delete index[m.report_id];
  }
}

/**
 * Prune in place: drop every track whose head aged past the live window,
 * then hold the track cap on the newest heads; un-index what goes.
 */
function pruneInPlace(
  byId: Record<string, ObjectTrackRecord>,
  index: Record<string, string>,
  opts: TrackMutationOptions | undefined,
): void {
  const now = opts?.now ?? Date.now();
  const floor = now - (opts?.retentionMs ?? DEFAULT_LIVE_WINDOW_MS);
  for (const [id, record] of Object.entries(byId)) {
    if (headTimeMs(record) < floor) {
      delete byId[id];
      unindex(index, record.members);
    }
  }
  const ids = Object.keys(byId);
  if (ids.length > OBJECT_TRACK_BUFFER_CAP) {
    ids.sort((a, b) => headTimeMs(byId[b]) - headTimeMs(byId[a]));
    for (const id of ids.slice(OBJECT_TRACK_BUFFER_CAP)) {
      unindex(index, byId[id].members);
      delete byId[id];
    }
  }
}

/**
 * The module-level object-track store. Consume via the narrow selectors below;
 * the feeders and the SSE bridge write via ``useObjectTrackStore.getState()``.
 */
export const useObjectTrackStore = create<ObjectTracksState>((set) => ({
  byId: {},
  trackIdByReportId: {},
  append: (key, member, opts) => {
    useObjectTrackStore.getState().appendMany([{ key, member }], opts);
  },
  appendMany: (entries, opts) =>
    set((state) => {
      const byId: Record<string, ObjectTrackRecord> = { ...state.byId };
      const index = state.trackIdByReportId;
      // One working copy of a track's members per batch, however many of its
      // members the batch carries.
      const working = new Map<string, TrackMember[]>();
      for (const { key, member } of entries) {
        let members = working.get(key.track_id);
        if (members === undefined) {
          members = [...(byId[key.track_id]?.members ?? [])];
          working.set(key.track_id, members);
        }
        placeMember(members, stamped(member), index[member.report_id] === key.track_id);
        index[member.report_id] = key.track_id;
        const existing = byId[key.track_id];
        if (existing === undefined) {
          byId[key.track_id] = {
            track_id: key.track_id,
            producer_id: key.producer_id,
            object_id: key.object_id,
            summary: null,
            members,
          };
        }
      }
      for (const [trackId, members] of working) {
        if (members.length > TRACK_MAX_MEMBERS) {
          unindex(index, members.splice(0, members.length - TRACK_MAX_MEMBERS));
        }
        byId[trackId] = { ...byId[trackId], members };
      }
      pruneInPlace(byId, index, opts);
      return { byId, trackIdByReportId: index };
    }),
  seed: (key, summary, members, opts) =>
    set((state) => {
      const index = state.trackIdByReportId;
      const existing = state.byId[key.track_id];
      const merged = mergeMembers(existing?.members ?? [], members);
      unindex(index, merged.dropped);
      for (const m of merged.members) {
        index[m.report_id] = key.track_id;
      }
      const record: ObjectTrackRecord = {
        track_id: key.track_id,
        producer_id: key.producer_id,
        object_id: key.object_id,
        summary: summary ?? existing?.summary ?? null,
        members: merged.members,
      };
      const byId = { ...state.byId, [key.track_id]: record };
      pruneInPlace(byId, index, opts);
      return { byId, trackIdByReportId: index };
    }),
  seedSummaries: (summaries, opts) =>
    set((state) => {
      const index = state.trackIdByReportId;
      const byId: Record<string, ObjectTrackRecord> = { ...state.byId };
      for (const summary of summaries) {
        const existing = byId[summary.track_id];
        byId[summary.track_id] = {
          track_id: summary.track_id,
          producer_id: summary.producer_id,
          object_id: summary.object_id,
          summary,
          members: existing?.members ?? [],
        };
      }
      pruneInPlace(byId, index, opts);
      return { byId, trackIdByReportId: index };
    }),
  clear: () => {
    set({ byId: {}, trackIdByReportId: {} });
  },
}));

// ---- Narrow-subscription selectors ----------------------------------

/** Every buffered record keyed by id (identity-stable between mutations). */
export function useObjectTracksById(): Record<string, ObjectTrackRecord> {
  return useObjectTrackStore((state) => state.byId);
}

/**
 * The tracks to *present* — two or more members — newest head first. Memoised
 * on ``byId`` so the array is referentially stable until the buffer changes.
 */
export function useLiveObjectTracks(): ObjectTrackRecord[] {
  const byId = useObjectTrackStore((state) => state.byId);
  return useMemo(
    () =>
      Object.values(byId)
        .filter(isPresentedTrack)
        .sort((a, b) => headTimeMs(b) - headTimeMs(a)),
    [byId],
  );
}

/** The buffered track with ``trackId``, or ``null``. */
export function useObjectTrackById(trackId: string | null): ObjectTrackRecord | null {
  return useObjectTrackStore((state) => (trackId === null ? null : (state.byId[trackId] ?? null)));
}

/** The ``track_id`` a buffered member belongs to, or ``null`` (the reverse index; never a scan). */
export function useTrackIdForDetection(reportId: string | null): string | null {
  return useObjectTrackStore((state) =>
    reportId === null ? null : (state.trackIdByReportId[reportId] ?? null),
  );
}
